<template>
  <div class="container-fluid dataset-not-found-page">
    <img
      v-if="isDatasetNotFound"
      class="catalog-page-background-image"
      src="@/assets/img/catalogue_fallback_img.avif"
      alt=""
    />
    <div class="row mt-1 mb-3">
      <div class="col-10 offset-1">
        <slot>
          <div v-if="isDatasetNotFound" class="dataset-unavailable-box mt-4" role="alert" aria-live="polite">
            <h2>Dataset not available</h2>
            <p>
              The dataset you are looking for can currently not be found or no longer exists.
            </p>
            <a class="new-search-link" href="https://data.europa.eu/data/combined">Start a new search</a>
          </div>

          <div v-else class="not-found">
            <h1>{{ $t('message.notFound.error') }}</h1>
            <p>{{ $t('message.notFound.message') }}</p>
            <p>{{ $t('message.notFound.consider') }}</p>
            <ul>
              <li>{{ $t('message.notFound.points.doubleCheck') }}</li>
              <li>
                <router-link :to="{ name: 'Datasets' }">{{ $t('message.notFound.points.newSearch') }}</router-link>
              </li>
              <li>
                <a class="footer-sub-text pt-0" :href="`/${$route.query.locale}/contact-us`">
                  {{ $t('message.notFound.points.reportError') }}
                </a>
              </li>
            </ul>
          </div>
        </slot>
        <div>
          <br><br><br>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { computed, onBeforeUnmount, onMounted, watch } from 'vue';
import { useHead } from '@unhead/vue'
import { useRoute } from 'vue-router';
import { AppLink, } from "@piveau/piveau-hub-ui-modules";

const DATASET_NOT_FOUND_DESCRIPTION = 'This dataset is no longer available on data.europa.eu. Browse the data catalogue to find other relevant datasets.';

const hasDatasetQuery = (query) => {
  const datasetParam = query?.dataset;
  if (Array.isArray(datasetParam)) return datasetParam.some((value) => String(value).trim().length > 0);
  return typeof datasetParam === 'string' && datasetParam.trim().length > 0;
};

const removeDatasetSpecificSeoTags = () => {
  if (typeof document === 'undefined') return;

  document.head.querySelectorAll('link[rel="canonical"]').forEach((el) => el.remove());
  document.head
    .querySelectorAll('link[rel="alternate"][hreflang]')
    .forEach((el) => el.remove());
  document.head
    .querySelectorAll('script[type="application/ld+json"]')
    .forEach((el) => el.remove());
};

export default {
  name: 'NotFound',
  components: {
    AppLink
  },
  computed: {
    isDatasetNotFound() {
      return hasDatasetQuery(this.$route.query);
    },
  },

  setup() {
    const route = useRoute();
    const isDatasetNotFound = computed(() => hasDatasetQuery(route.query));
    let seoCleanupObserver = null;

    useHead(() => ({
      title: isDatasetNotFound.value
        ? 'Dataset not available | data.europa.eu'
        : undefined,
      meta: [
        {
          name: 'robots',
          content: 'noindex, nofollow'
        },
        ...(isDatasetNotFound.value
          ? [{
            name: 'description',
            content: DATASET_NOT_FOUND_DESCRIPTION,
          }]
          : []),
      ],
    }))

    const cleanupDatasetSeo = () => {
      if (!isDatasetNotFound.value) return;
      removeDatasetSpecificSeoTags();
    };

    watch(
      () => route.fullPath,
      () => {
        cleanupDatasetSeo();
      },
      { immediate: true }
    );

    onMounted(() => {
      cleanupDatasetSeo();

      if (typeof MutationObserver !== 'undefined') {
        seoCleanupObserver = new MutationObserver(() => {
          cleanupDatasetSeo();
        });

        seoCleanupObserver.observe(document.head, {
          childList: true,
          subtree: true,
          attributes: true,
          attributeFilter: ['href', 'rel', 'hreflang', 'type'],
        });
      }
    });

    onBeforeUnmount(() => {
      if (seoCleanupObserver) {
        seoCleanupObserver.disconnect();
      }
    });

  },
  mounted() {
    if (this.$piwik) {
      this.$piwik.trackInteraction('not_found', {
        screen_title: window.title,
        page_url: window.location.href,
        dataset_ID: this.$route.query.dataset || '',
      });
    }
  },
  created() {
    this.$router.isReady().then(() => {
      if (!this.$route.query.locale) {
        const locale = navigator.language.substring(0, 2);
        this.$root.$i18n.locale = locale;
        this.$router.push({
          query: {
            ...this.$route.query,
            locale
          }
        });
      }
    });
  },
};
</script>

<style lang="scss" scoped>
.dataset-not-found-page {
  position: relative;
  min-height: 420px;
  padding-left: 0;
  padding-right: 0;
}

.dataset-not-found-page .row {
  margin-left: 0;
  margin-right: 0;
}

.catalog-page-background-image {
  position: absolute;
  height: 360px;
  width: 100vw;
  max-width: none;
  top: 0;
  left: 50%;
  transform: translateX(-50%);
  overflow: hidden;
  -o-object-fit: cover;
  object-fit: cover;
  filter: brightness(.9);
  opacity: 0.2;
  pointer-events: none;
}

.dataset-unavailable-box {
  position: relative;
  z-index: 1;
  max-width: 720px;
  margin: 2rem auto 3rem;
  padding: 1rem 1.25rem;
  border-radius: 6px;
  background: #f4f8fa;
  border-left: 4px solid #0E47CB;

  h2 {
    font-size: 1.6rem;
    margin-bottom: 0.75rem;
  }

  p {
    margin-bottom: 1rem;
  }
}

.new-search-link {
  font-weight: 600;
}
</style>
